import React from "react";
import { Box, Grid, TextField, Typography } from "@mui/material";
import ReactQuill from "react-quill";
import PropTypes from "prop-types";
import { fieldStyles } from "services/utils";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
  ],
};

const getFieldSx = (fieldName, saveStatus) => ({
  ...fieldStyles,
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor:
        saveStatus[fieldName] === "success"
          ? "green"
          : saveStatus[fieldName] === "error"
            ? "red"
            : undefined,
    },
    "& fieldset": {
      borderColor:
        saveStatus[fieldName] === "success"
          ? "green"
          : saveStatus[fieldName] === "error"
            ? "red"
            : undefined,
    },
  },
});

const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "align"];

export const JobForm = ({ employeeDetails, setEmployeeDetails, saveStatus, handleFieldSave }) => {
  const { t } = useTranslation();

  return (
    <Box mt={0}>
      <Typography variant="h5" mb={1} mt={2}>
        {t("admin.employees.employeeDialog.employeeDetails.jobInfo")}
      </Typography>

      <Grid container spacing={2}>
        {/* Employee Function / Job Purpose */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ ml: 1 }}>
            {t("admin.employees.employeeDialog.employeeDetails.empFunction")}
          </Typography>

          <Box
            sx={{
              border: "2px solid",
              borderRadius: 2,
              backgroundColor: "#fff",
              overflow: "hidden",
              borderColor:
                saveStatus["employeeFunction"] === "success"
                  ? "green"
                  : saveStatus["employeeFunction"] === "error"
                    ? "red"
                    : "#ccc",
              ".ql-toolbar": {
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottom: "1px solid #ccc",
              },
              ".ql-container": {
                minHeight: "150px",
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                paddingBottom: "16px",
              },
            }}
          >
            <ReactQuill
              value={employeeDetails.employeeFunction || ""}
              onChange={(value) =>
                setEmployeeDetails((prev) => ({ ...prev, employeeFunction: value }))
              }
              onBlur={() =>
                handleFieldSave(employeeDetails.id, "employeeFunction", {
                  employeeFunction: employeeDetails.employeeFunction,
                })
              }
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
            />
          </Box>
        </Grid>

        {/* Skill Pool */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.skillPool")}
            value={employeeDetails.skillPool || ""}
            onChange={(e) => setEmployeeDetails((prev) => ({ ...prev, skillPool: e.target.value }))}
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "skillPool", {
                skillPool: e.target.value,
              })
            }
            sx={getFieldSx("skillPool", saveStatus)}
          />
        </Grid>

        {/* Work Area */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.workArea")}
            value={employeeDetails.workArea || ""}
            onChange={(e) => setEmployeeDetails((prev) => ({ ...prev, workArea: e.target.value }))}
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "workArea", {
                workArea: e.target.value,
              })
            }
            sx={getFieldSx("workArea", saveStatus)}
          />
        </Grid>

        {/* PIR Position in Range */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.pir")}
            value={employeeDetails.pirPositionInRange || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, pirPositionInRange: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "pirPositionInRange", {
                pirPositionInRange: e.target.value,
              })
            }
            sx={getFieldSx("pirPositionInRange", saveStatus)}
          />
        </Grid>

        {/* Customized Code */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.customizedCode")}
            value={employeeDetails.customizedCode || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, customizedCode: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "customizedCode", {
                customizedCode: e.target.value,
              })
            }
            sx={getFieldSx("customizedCode", saveStatus)}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5" mb={1} mt={1}>
            {t("admin.employees.employeeDialog.employeeDetails.employmentInfo")}
          </Typography>
        </Grid>

        {/* Shifts */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.shifts")}
            value={employeeDetails.shifts || ""}
            onChange={(e) => setEmployeeDetails((prev) => ({ ...prev, shifts: e.target.value }))}
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "shifts", { shifts: e.target.value })
            }
            sx={getFieldSx("shifts", saveStatus)}
          />
        </Grid>

        {/* Appraisal Period */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.appraisalPeriod")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.appraisalPeriod || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, appraisalPeriod: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "appraisalPeriod", {
                appraisalPeriod: e.target.value,
              })
            }
            sx={getFieldSx("appraisalPeriod", saveStatus)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

JobForm.propTypes = {
  employeeDetails: PropTypes.object.isRequired,
  setEmployeeDetails: PropTypes.func.isRequired,
  saveStatus: PropTypes.object.isRequired,
  handleFieldSave: PropTypes.func.isRequired,
};

export const PositionForm = ({
  employeeDetails,
  setEmployeeDetails,
  saveStatus,
  handleFieldSave,
}) => {
  const { t } = useTranslation();

  return (
    <Box mt={2}>
      <Grid container spacing={2}>
        {/* Contract Type */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.contractType")}
            value={employeeDetails.contractType || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, contractType: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "contractType", { contractType: e.target.value })
            }
            sx={getFieldSx("contractType", saveStatus)}
          />
        </Grid>

        {/* Contract Date */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label={t("admin.employees.employeeDialog.employeeDetails.contractDate")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.contractDate || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, contractDate: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "contractDate", { contractDate: e.target.value })
            }
            sx={getFieldSx("contractDate", saveStatus)}
          />
        </Grid>

        {/* Probation Date */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label={t("admin.employees.employeeDialog.employeeDetails.probation")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.probationDate || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, probationDate: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "probationDate", {
                probationDate: e.target.value,
              })
            }
            sx={getFieldSx("probationDate", saveStatus)}
          />
        </Grid>

        {/* Contract Renewal Date */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label={t("admin.employees.employeeDialog.employeeDetails.contractRenewalDate")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.contractRenewalDate || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, contractRenewalDate: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "contractRenewalDate", {
                contractRenewalDate: e.target.value,
              })
            }
            sx={getFieldSx("contractRenewalDate", saveStatus)}
          />
        </Grid>

        {/* First Day Payroll */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label={t("admin.employees.employeeDialog.employeeDetails.firstPayroll")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.firstDayPayroll || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, firstDayPayroll: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "firstDayPayroll", {
                firstDayPayroll: e.target.value,
              })
            }
            sx={getFieldSx("firstDayPayroll", saveStatus)}
          />
        </Grid>

        {/* Date Joining Department */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label={t("admin.employees.employeeDialog.employeeDetails.dateJoin")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.dateJoiningDepartment || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, dateJoiningDepartment: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "dateJoiningDepartment", {
                dateJoiningDepartment: e.target.value,
              })
            }
            sx={getFieldSx("dateJoiningDepartment", saveStatus)}
          />
        </Grid>

        {/* Service Years */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.service")}
            value={employeeDetails.serviceYears || ""}
            onChange={(e) => {
              const val = Math.max(0, Number(e.target.value));
              setEmployeeDetails((prev) => ({ ...prev, serviceYears: val }));
            }}
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "serviceYears", { serviceYears: e.target.value })
            }
            inputProps={{ min: 0 }}
            sx={getFieldSx("serviceYears", saveStatus)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

PositionForm.propTypes = {
  employeeDetails: PropTypes.object.isRequired,
  setEmployeeDetails: PropTypes.func.isRequired,
  saveStatus: PropTypes.object.isRequired,
  handleFieldSave: PropTypes.func.isRequired,
};
